import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Collapse, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  LEAD_ITEM_KIND_LABELS,
  interestStageTouchesStock,
  isInterestTrackable,
  getInquiryDisplayAt,
  getLeadItemLabelParts,
  getLeadItemOpenLabel,
  getLeadItemTarget,
  groupLeadInterests,
} from '../leadsConfig';
import { formatDateTimeShortAR } from '../../Utils/dateTimeFormat';
import {
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  LEADS_TEXT_SECONDARY,
  leadsSectionTitleSx,
} from './leadsUi.styles';
import LeadInterestStageMenu from './LeadInterestStageMenu';
import LeadInterestStageDialog from './LeadInterestStageDialog';
import LeadLostReasonDialog from './LeadLostReasonDialog';
import LeadInquiryCard from './LeadInquiryCard';

/**
 * Texto del chip de un interés: "Lote: Lote 42". Si el label es solo la
 * nomenclatura catastral: "Lote 1101010412018022" (sin repetir el tipo).
 *
 * @param {object} item
 * @returns {string}
 */
export function formatLeadItemChipLabel(item) {
  const parts = getLeadItemLabelParts(item?.item_label, item?.item_kind);
  if (parts.isCadastral) return parts.text;
  return `${LEAD_ITEM_KIND_LABELS[item?.item_kind] || 'Item'}: ${parts.text || '—'}`;
}

/**
 * Contenido del chip: con nomenclatura sola, el número va apagado después del
 * tipo (“Lote” + número secundario). Si no, el texto de siempre.
 *
 * @param {object} item
 * @returns {React.ReactNode}
 */
export function renderLeadItemChipLabel(item) {
  const parts = getLeadItemLabelParts(item?.item_label, item?.item_kind);
  if (!parts.isCadastral) return formatLeadItemChipLabel(item);
  return (
    <>
      {parts.primary}
      {' '}
      <Box component="span" sx={{ color: LEADS_TEXT_SECONDARY, fontVariantNumeric: 'tabular-nums' }}>
        {parts.secondary}
      </Box>
    </>
  );
}

/** Chip de interés (también lo usa el alta para el ítem precargado). */
export const leadItemChipSx = {
  bgcolor: 'rgba(255,255,255,0.08)',
  color: LEADS_TEXT_PRIMARY,
  fontSize: '12px',
  maxWidth: '100%',
  '&.MuiChip-clickable:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
  '& .MuiChip-icon': { color: LEADS_TEXT_SECONDARY, fontSize: 14, ml: '6px' },
  '& .MuiChip-deleteIcon': { color: LEADS_TEXT_SECONDARY, '&:hover': { color: '#FFFFFF' } },
};

/**
 * Texto del checkbox de stock para `from → to` (V3-3). '' → sin checkbox.
 *
 * @param {object} item
 * @param {string} from
 * @param {string} to
 */
export function getInterestStockLabel(item, from, to) {
  if (!interestStageTouchesStock(item, from, to)) return '';
  const what = item?.item_kind === 'cad_parcel' ? 'la parcela' : 'la unidad';
  if (to === 'reservado') return `Marcar ${what} como reservada en el stock`;
  if (to === 'cerrado') return `Marcar ${what} como vendida en el stock`;
  return `Devolver el stock de ${what} a como estaba`;
}

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  px: 1.5,
  py: 1.25,
  borderRadius: '8px',
  bgcolor: 'rgba(255,255,255,0.04)',
};

const kindSx = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: LEADS_TEXT_MUTED,
  lineHeight: 1.2,
};

const rowLabelSx = {
  minWidth: 0,
  maxWidth: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'left',
  mt: 0.25,
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.3,
  color: LEADS_TEXT_PRIMARY,
  borderRadius: '4px',
  '&:not(.Mui-disabled):hover .lead-item-row-text': {
    color: '#FFFFFF',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(255,255,255,0.3)',
  },
};

const toggleSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 0.25,
  px: 0,
  py: 0.25,
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 500,
  color: LEADS_TEXT_SECONDARY,
  textAlign: 'left',
  '& .MuiSvgIcon-root': { fontSize: 16, color: LEADS_TEXT_MUTED, transition: 'transform 150ms', flexShrink: 0 },
  '&:hover': { color: '#FFFFFF' },
};

export function formatInquiryCount(n) {
  return n === 1 ? '1 consulta' : `${n} consultas`;
}

/**
 * Consultas colapsables (cerradas por defecto): "N consultas · última …".
 * `onExpand` se llama al abrir (p. ej. marcar vistas en la bandeja).
 *
 * @param {{ inquiries: object[], total?: number, compact?: boolean, label?: string,
 *   testId?: string, onExpand?: (rows: object[]) => void }} props
 */
export function InquiriesCollapse({ inquiries, total, compact, label, testId, onExpand, onAssignRecord = null }) {
  const [open, setOpen] = useState(false);
  if (!inquiries.length) return null;
  const last = formatDateTimeShortAR(getInquiryDisplayAt(inquiries[0]));
  const shownTotal = Number.isFinite(Number(total)) ? Number(total) : inquiries.length;
  return (
    <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <ButtonBase
        aria-expanded={open}
        onClick={() => setOpen((v) => {
          const next = !v;
          if (next && onExpand) onExpand(inquiries);
          return next;
        })}
        sx={{ ...toggleSx, '& .MuiSvgIcon-root': { ...toggleSx['& .MuiSvgIcon-root'], transform: open ? 'rotate(90deg)' : 'none' } }}
      >
        <ChevronRightIcon />
        <Box component="span" sx={{ minWidth: 0 }}>
          {label || formatInquiryCount(shownTotal)}
          {last ? <Box component="span" sx={{ color: LEADS_TEXT_MUTED, fontWeight: 400 }}>{` · última ${last}`}</Box> : null}
        </Box>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {inquiries.map((inq, index) => (
            <LeadInquiryCard
              key={inq.id || `${inq.created_at}-${index}`}
              inquiry={inq}
              compact={compact}
              onAssignRecord={onAssignRecord}
            />
          ))}
          {shownTotal > inquiries.length && (
            <Typography data-testid="lead-inquiries-truncated" sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED, px: 0.75 }}>
              {`Mostrando las últimas ${inquiries.length} de ${total} consultas.`}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Sección "Intereses y consultas" del detalle. Una tarjeta por interés: el
 * tipo arriba, el nombre (abre el lote / lista con `onOpen`), la etapa (V3-3)
 * a la derecha y, si hay, las consultas colapsables adentro de la misma
 * tarjeta (`items[].inquiries`). Las consultas raíz sin interés van en
 * "Otras consultas". Con
 * `onChangeStage` la etapa abre el menú; ofertado pide monto, descartado el
 * motivo y reservado / cerrado (o salir de ellas) el checkbox de stock,
 * destildado. Listas / showrooms no tienen etapa.
 *
 * `recordFilter` recorta esta sección junto con la actividad: `'all'`, el id
 * de un interés, o `'others'`.
 *
 * @param {{ items: object[], inquiries?: object[]|null, openingItemId?: string,
 *   recordFilter?: string,
 *   onOpen?: ((item: object) => void)|null,
 *   savingItemId?: string,
 *   onChangeStage?: ((item: object, change: object) => Promise<boolean>)|null,
 *   headerAction?: React.ReactNode, onAssignRecord?: ((inquiry: object) => void)|null,
 *   emptyHint?: string }} props
 *   `headerAction`: botón a la derecha del título (p. ej. «Nueva consulta»).
 *   `onAssignRecord`: «Asignar registro» en consultas manuales abiertas.
 */
export default function LeadItemChips({
  items,
  inquiries = null,
  openingItemId = '',
  recordFilter = 'all',
  onOpen = null,
  savingItemId = '',
  onChangeStage = null,
  headerAction = null,
  onAssignRecord = null,
  emptyHint = 'Se suman solos cuando consulta desde un link compartido.',
}) {
  // { item, stage, kind: 'offer' | 'discard' | 'stock' }
  const [pending, setPending] = useState(null);
  const busy = Boolean(pending && savingItemId === pending.item.id);

  const submit = async (item, change) => {
    if (!onChangeStage) return;
    const done = await onChangeStage(item, change);
    if (done) setPending(null);
  };

  const handleSelect = (item, stage) => {
    const from = item.stage;
    if (stage === 'ofertado') { setPending({ item, stage, kind: 'offer' }); return; }
    if (stage === from) return;
    if (stage === 'descartado') { setPending({ item, stage, kind: 'discard' }); return; }
    if (getInterestStockLabel(item, from, stage)) { setPending({ item, stage, kind: 'stock' }); return; }
    submit(item, { stage, applyStock: false });
  };

  const pendingLabel = pending ? getLeadItemLabelParts(pending.item.item_label, pending.item.item_kind).text : '';
  const pendingStock = pending ? getInterestStockLabel(pending.item, pending.item.stage, pending.stage) : '';

  const grouped = groupLeadInterests(items, inquiries);
  const groups = recordFilter === 'all'
    ? grouped.groups
    : recordFilter === 'others'
      ? []
      : grouped.groups.filter((group) => group.item?.id === recordFilter);
  const others = recordFilter === 'all' || recordFilter === 'others' ? grouped.others : [];

  return (
    <Box data-testid="lead-inquiries">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, minHeight: 26 }}>
        <Typography sx={leadsSectionTitleSx}>Intereses y consultas</Typography>
        {headerAction}
      </Box>
      {groups.length ? (
        <Box component="ul" aria-label="Intereses y consultas" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {groups.map(({ item, inquiries: itemInquiries, total }) => {
            const parts = getLeadItemLabelParts(item.item_label, item.item_kind);
            const kind = LEAD_ITEM_KIND_LABELS[item.item_kind] || 'Item';
            const title = parts.isCadastral ? parts.secondary : (parts.text || '—');
            const target = onOpen ? getLeadItemTarget(item) : null;
            const openLabel = getLeadItemOpenLabel(target);
            const opening = openingItemId === item.id;
            const tip = openLabel || '';
            const trackable = isInterestTrackable(item) && Boolean(item.stage);
            const labelNode = (
              <ButtonBase
                disabled={!target || opening}
                onClick={target ? () => onOpen(item) : undefined}
                aria-label={target ? `${openLabel}: ${parts.text || title}` : undefined}
                sx={rowLabelSx}
              >
                {opening ? <CircularProgress size={12} sx={{ color: 'inherit', mr: 0.75 }} /> : null}
                <Box
                  component="span"
                  className="lead-item-row-text"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ...(parts.isCadastral ? { fontWeight: 500, color: LEADS_TEXT_SECONDARY, fontVariantNumeric: 'tabular-nums' } : {}),
                  }}
                >
                  {title}
                </Box>
              </ButtonBase>
            );
            return (
              <Box component="li" key={item.id} data-testid="lead-interest" sx={cardSx}>
                <Box data-testid="lead-item-row" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={kindSx}>{kind}</Typography>
                    {tip ? <Tooltip title={tip} placement="top" arrow>{labelNode}</Tooltip> : labelNode}
                  </Box>
                  {trackable ? (
                    <LeadInterestStageMenu
                      item={item}
                      editable={Boolean(onChangeStage)}
                      busy={savingItemId === item.id}
                      onSelect={(stage) => handleSelect(item, stage)}
                    />
                  ) : null}
                </Box>
                {itemInquiries.length > 0 && (
                  <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <InquiriesCollapse inquiries={itemInquiries} total={total} compact testId="lead-interest-inquiries" />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ) : !others.length ? (
        <Typography sx={{ fontSize: '12px', color: LEADS_TEXT_MUTED }}>
          {recordFilter === 'all'
            ? emptyHint
            : 'Sin consultas de este registro.'}
        </Typography>
      ) : null}
      {others.length > 0 && (
        <Box data-testid="lead-other-inquiries" sx={{ mt: groups.length ? 1.25 : 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: LEADS_TEXT_SECONDARY }}>
            {`Otras consultas (${others.length})`}
          </Typography>
          <InquiriesCollapse
            inquiries={others}
            total={others.length}
            label={formatInquiryCount(others.length)}
            testId="lead-other-inquiries-list"
            onAssignRecord={onAssignRecord}
          />
        </Box>
      )}

      <LeadInterestStageDialog
        open={Boolean(pending && (pending.kind === 'offer' || pending.kind === 'stock'))}
        stage={pending?.stage || ''}
        itemLabel={pendingLabel}
        stockLabel={pendingStock}
        initialAmount={pending?.item?.offer_amount ?? null}
        initialCurrency={pending?.item?.offer_currency ?? null}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={(change) => submit(pending.item, { stage: pending.stage, ...change })}
      />
      <LeadLostReasonDialog
        open={Boolean(pending && pending.kind === 'discard')}
        itemLabel={pendingLabel}
        stockLabel={pendingStock}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={(reason, note, applyStock) => submit(pending.item, {
          stage: 'descartado', discardReason: reason, note, applyStock,
        })}
      />
    </Box>
  );
}
